import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ImageUpload = ({ 
  value, 
  onChange, 
  onUploadComplete,
  placeholder = "URL de l'image ou télécharger",
  accept = "image/*,.pdf,.doc,.docx",
  maxSize = 20 * 1024 * 1024 // 20MB
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxSize) {
      toast.error(`Fichier trop volumineux. Maximum: ${maxSize / (1024 * 1024)}MB`);
      return;
    }

    // Show preview immediately for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }

    // Upload file
    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_BASE}/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      const uploadedUrl = response.data.url;
      setPreview(uploadedUrl);
      onChange(uploadedUrl);
      
      if (onUploadComplete) {
        onUploadComplete(response.data);
      }
      
      toast.success('Image téléchargée avec succès !');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors du téléchargement');
      setPreview(value || '');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    onChange(url);
    setPreview(url);
  };

  const handleRemove = () => {
    setPreview('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-3">
      {/* URL Input with upload button */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            type="text"
            placeholder={placeholder}
            value={value || ''}
            onChange={handleUrlChange}
            className="pr-10"
            disabled={isUploading}
          />
          {value && (
            <button
              type="button"
              onClick={handleRemove}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-red-500"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={triggerFileInput}
          disabled={isUploading}
          className="shrink-0"
        >
          {isUploading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Upload size={18} />
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Upload progress */}
      {isUploading && (
        <div className="w-full bg-neutral-200 rounded-full h-2">
          <div
            className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="relative rounded-lg overflow-hidden border border-neutral-200 bg-neutral-50">
          {preview.match(/\.(jpg|jpeg|png|gif|webp)$/i) || preview.startsWith('data:image') || preview.includes('/uploads/') ? (
            <div className="relative aspect-video">
              <img
                src={preview}
                alt="Aperçu"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="absolute inset-0 hidden items-center justify-center bg-neutral-100 text-neutral-400">
                <ImageIcon size={48} />
              </div>
            </div>
          ) : (
            <div className="p-4 flex items-center gap-3">
              <ImageIcon size={24} className="text-neutral-400" />
              <span className="text-sm text-neutral-600 truncate">{preview}</span>
            </div>
          )}
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Drop zone hint */}
      {!preview && !isUploading && (
        <div
          onClick={triggerFileInput}
          className="border-2 border-dashed border-neutral-300 rounded-lg p-6 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors"
        >
          <Upload size={32} className="mx-auto text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-500">
            Cliquez pour télécharger ou glissez-déposez
          </p>
          <p className="text-xs text-neutral-400 mt-1">
            PNG, JPG, GIF jusqu'à {maxSize / (1024 * 1024)}MB
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
